import React, { memo } from 'react';
import { TaskPriority } from '../../types';
import { PRIORITY_COLORS } from '../../utils/constants';

// ============================================
// Types
// ============================================

interface PrioritySelectorProps {
  value: TaskPriority;
  onChange: (priority: TaskPriority) => void;
  disabled?: boolean;
}

interface PriorityBadgeProps {
  priority: TaskPriority;
  size?: 'sm' | 'md';
}

// ============================================
// Priority Utilities
// ============================================

export const getPriorityColor = (priority: TaskPriority): string => {
  return PRIORITY_COLORS[priority] || PRIORITY_COLORS.default;
};

const priorities: TaskPriority[] = ['Low', 'Medium', 'High'];

// ============================================
// Priority Badge Component
// ============================================

export const PriorityBadge: React.FC<PriorityBadgeProps> = memo(({ priority, size = 'sm' }) => {
  const sizeClasses = size === 'sm' 
    ? 'px-2 py-0.5 text-[8px] md:text-[9px]' 
    : 'px-3 py-1 text-[10px]';
  
  return (
    <span 
      className={`
        ${sizeClasses}
        rounded-lg font-black uppercase tracking-widest text-white
        ${getPriorityColor(priority)}
      `}
    >
      {priority}
    </span>
  );
});

PriorityBadge.displayName = 'PriorityBadge';

// ============================================
// Priority Selector Component
// ============================================

export const PrioritySelector: React.FC<PrioritySelectorProps> = memo(({ 
  value, 
  onChange,
  disabled = false,
}) => {
  return (
    <div className="flex gap-2">
      {priorities.map(priority => {
        const isSelected = value === priority;
        const colorClass = getPriorityColor(priority);
        
        return (
          <button
            key={priority}
            type="button"
            onClick={() => onChange(priority)}
            disabled={disabled}
            className={`
              flex-1 py-3 md:py-4 rounded-2xl 
              text-[9px] md:text-[11px] font-black uppercase tracking-widest 
              transition-all
              disabled:opacity-50 disabled:cursor-not-allowed
              ${isSelected 
                ? `${colorClass} text-white shadow-lg` 
                : 'bg-slate-50 text-slate-400 border border-slate-200 hover:bg-slate-100'
              }
            `}
          >
            {priority}
          </button>
        );
      })}
    </div>
  );
});

PrioritySelector.displayName = 'PrioritySelector';

export default PrioritySelector;
